import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Box,
  Avatar,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
  Paper,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';

const BookingDialog = ({
  open,
  onClose,
  editingBooking,
  bookingForm,
  setBookingForm,
  userProfile,
  onSave
}) => {
  const theme = useTheme();
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showScrollArrow, setShowScrollArrow] = useState(true);

  const handleServiceChange = (event) => {
    setBookingForm(prev => ({ ...prev, service: event.target.value }));
  };

  // Handle scroll arrow visibility
  useEffect(() => {
    if (!open || !isMobile) {
      setShowScrollArrow(false);
      return;
    }

    const handleScroll = (e) => {
      const element = e.target;
      const scrollTop = element.scrollTop;
      const scrollHeight = element.scrollHeight;
      const clientHeight = element.clientHeight;
      
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        setShowScrollArrow(false);
      } else {
        setShowScrollArrow(true);
      }
    };

    const dialogContent = document.querySelector('[role="dialog"] .MuiDialogContent-root');
    if (dialogContent) {
      dialogContent.addEventListener('scroll', handleScroll);
      // Initial check
      const scrollHeight = dialogContent.scrollHeight;
      const clientHeight = dialogContent.clientHeight;
      setShowScrollArrow(scrollHeight > clientHeight);
      
      return () => dialogContent.removeEventListener('scroll', handleScroll);
    }
  }, [open, isMobile]);

  const scrollToBottom = () => {
    const dialogContent = document.querySelector('[role="dialog"] .MuiDialogContent-root');
    if (dialogContent) {
      dialogContent.scrollTo({
        top: dialogContent.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isSmallMobile}
      PaperProps={{
        sx: {
          borderRadius: isSmallMobile ? 0 : 3,
          m: isSmallMobile ? 0 : 1,
          maxHeight: isSmallMobile ? '100vh' : '90vh',
          overflow: 'hidden'
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
            color: 'white',
            p: { xs: 1.5, sm: 4 },
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Background decoration */}
          <Box sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 100,
            height: 100,
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '50%',
            opacity: 0.6
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 80,
            height: 80,
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '50%'
          }} />
          
          <Box display="flex" alignItems="center" gap={{ xs: 2, sm: 3 }} position="relative" zIndex={1}>
            <Avatar sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              width: { xs: 48, sm: 56 },
              height: { xs: 48, sm: 56 },
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255,255,255,0.3)'
            }}>
              <CalendarIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h5"
                fontWeight="700"
                sx={{
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  mb: 0.5,
                  letterSpacing: '-0.02em'
                }}
              >
                {editingBooking ? 'Modifier la réservation' : 'Nouvelle réservation'}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  opacity: 0.9,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: 400
                }}
              >
                {editingBooking ? 'Modifiez les détails de la réservation' : 'Ajoutez une nouvelle réservation à votre planning'}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              sx={{
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.2)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, overflow: 'auto', position: 'relative' }}>
        <Box p={{ xs: 1.5, sm: 4 }} sx={{ bgcolor: '#fafafa' }}>
          <Grid container spacing={{ xs: 1.5, sm: 3 }}>
            {/* Section Client */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 3 },
                  mb: { xs: 2, sm: 3 },
                  bgcolor: 'white',
                  borderRadius: 3,
                  border: '1px solid #e5e7eb'
                }}
              >
                <Box display="flex" alignItems="center" gap={2} mb={{ xs: 2, sm: 3 }}>
                  <Avatar sx={{
                    bgcolor: '#059669',
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 },
                    boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)'
                  }}>
                    <PersonIcon fontSize="small" />
                  </Avatar>
                  <Typography
                    variant="h6"
                    fontWeight="700"
                    sx={{
                      color: '#1f2937',
                      fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                  >
                    Informations client
                  </Typography>
                </Box>
                
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nom du client"
                      value={bookingForm.clientName}
                      onChange={(e) => setBookingForm(prev => ({ ...prev, clientName: e.target.value }))}
                      required
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: '#059669'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#059669'
                          }
                        }
                      }}
                      InputProps={{
                        startAdornment: <PersonIcon sx={{ mr: 1, color: '#6b7280' }} />
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Téléphone"
                      value={bookingForm.clientPhone}
                      onChange={(e) => setBookingForm(prev => ({ ...prev, clientPhone: e.target.value }))}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: '#059669'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#059669'
                          }
                        }
                      }}
                      InputProps={{
                        startAdornment: <PhoneIcon sx={{ mr: 1, color: '#6b7280' }} />
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email (optionnel)"
                      type="email"
                      value={bookingForm.clientEmail}
                      onChange={(e) => setBookingForm(prev => ({ ...prev, clientEmail: e.target.value }))}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: '#059669'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#059669'
                          }
                        }
                      }}
                      InputProps={{
                        startAdornment: <EmailIcon sx={{ mr: 1, color: '#6b7280' }} />
                      }}
                      helperText="L'email permet d'envoyer la confirmation et les rappels automatiques"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Section Service */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  mb: 3,
                  bgcolor: 'white',
                  borderRadius: 3,
                  border: '1px solid #e5e7eb'
                }}
              >
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Avatar sx={{
                    bgcolor: '#dc2626',
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 },
                    boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)'
                  }}>
                    <BusinessIcon fontSize="small" />
                  </Avatar>
                  <Typography
                    variant="h6"
                    fontWeight="700"
                    sx={{
                      color: '#1f2937',
                      fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                  >
                    Service et planning
                  </Typography>
                </Box>
                
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  <Grid item xs={12}>
                    <FormControl fullWidth required variant="outlined">
                      <InputLabel>Service</InputLabel>
                      <Select
                        value={bookingForm.service}
                        label="Service"
                        onChange={handleServiceChange}
                        sx={{
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#059669'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#059669'
                          }
                        }}
                        startAdornment={<BusinessIcon sx={{ mr: 1, color: '#6b7280' }} />}
                      >
                        {userProfile?.services?.length > 0 ? (
                          userProfile.services.map((service) => (
                            <MenuItem key={service.id} value={service.name}>
                              <Box>
                                <Typography variant="body1" fontWeight="600">
                                  {service.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {service.price}€ • {service.duration} min
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled>
                            <Typography color="text.secondary">
                              Aucun service configuré. Ajoutez vos services dans l'onglet "Horaires & Services".
                            </Typography>
                          </MenuItem>
                        )}
                      </Select>
                      {(!userProfile?.services || userProfile.services.length === 0) && (
                        <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                          Vous devez d'abord configurer vos services dans l'onglet "Horaires & Services"
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Date du rendez-vous"
                      value={bookingForm.date && dayjs.isDayjs(bookingForm.date) && bookingForm.date.isValid() ? bookingForm.date : null}
                      onChange={(date) => setBookingForm(prev => ({ ...prev, date }))}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          variant: 'outlined',
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover fieldset': {
                                borderColor: '#059669'
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#059669'
                              }
                            }
                          },
                          InputProps: {
                            startAdornment: <CalendarIcon sx={{ mr: 1, color: '#6b7280' }} />
                          }
                        }
                      }}
                      minDate={dayjs()}
                      format="DD/MM/YYYY"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TimePicker
                      label="Heure du rendez-vous"
                      value={bookingForm.time && dayjs.isDayjs(bookingForm.time) && bookingForm.time.isValid() ? bookingForm.time : null}
                      onChange={(time) => setBookingForm(prev => ({ ...prev, time }))}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          variant: 'outlined',
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover fieldset': {
                                borderColor: '#059669'
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#059669'
                              }
                            }
                          },
                          InputProps: {
                            startAdornment: <ScheduleIcon sx={{ mr: 1, color: '#6b7280' }} />
                          }
                        }
                      }}
                      format="HH:mm"
                      ampm={false}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Notes (optionnel)"
                      multiline
                      rows={3}
                      value={bookingForm.notes}
                      onChange={(e) => setBookingForm(prev => ({ ...prev, notes: e.target.value }))}
                      variant="outlined"
                      placeholder="Informations complémentaires, demandes spéciales..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: '#059669'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#059669'
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

          </Grid>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{
        p: { xs: 3, sm: 4 },
        pt: 0,
        bgcolor: '#fafafa',
        borderTop: '1px solid #e5e7eb'
      }}>
        <Box
          display="flex"
          gap={{ xs: 2, sm: 3 }}
          width="100%"
          flexDirection={isSmallMobile ? 'column' : 'row'}
          justifyContent="flex-end"
        >
          <Button
            onClick={onClose}
            variant="outlined"
            size="large"
            fullWidth={isSmallMobile}
            sx={{
              minWidth: isSmallMobile ? 'auto' : 140,
              borderRadius: 2,
              borderColor: '#d1d5db',
              color: '#6b7280',
              fontWeight: 600,
              py: { xs: 1.5, sm: 1.75 },
              '&:hover': {
                borderColor: '#9ca3af',
                bgcolor: '#f9fafb'
              }
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={onSave}
            variant="contained"
            size="large"
            fullWidth={isSmallMobile}
            disabled={!bookingForm.clientName || !bookingForm.service || !bookingForm.date || !bookingForm.time}
            sx={{
              minWidth: isSmallMobile ? 'auto' : 140,
              bgcolor: '#059669',
              borderRadius: 2,
              fontWeight: 700,
              py: { xs: 1.5, sm: 1.75 },
              boxShadow: '0 4px 12px rgba(5, 150, 105, 0.4)',
              '&:hover': {
                bgcolor: '#047857',
                boxShadow: '0 6px 16px rgba(5, 150, 105, 0.5)'
              },
              '&:disabled': {
                bgcolor: '#d1d5db',
                boxShadow: 'none'
              }
            }}
          >
            {editingBooking ? 'Modifier la réservation' : 'Ajouter la réservation'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default BookingDialog;